import _ from 'lodash';
import { z } from 'zod';
import { AppType, Perspective } from './general';
export var FabType;
(function (FabType) {
    FabType["none"] = "none";
    FabType["buttonModal"] = "buttonModal";
    FabType["worklist"] = "worklist";
    FabType["cart"] = "cart";
    FabType["planning"] = "planning";
    FabType["postTextModal"] = "postTextModal";
    FabType["pivot"] = "pivot";
})(FabType || (FabType = {}));
export const zHasTopMembers = z.object({ topMembers: z.string().optional() });
export const zHasWorklistFunctionality = z.object({ allowWorklistFunctionality: z.boolean().optional() });
// FIXME: should really be called HasStylePane with a prop of showStylePane to be more clear
export const zHasPopover = z.object({ showPopover: z.boolean().optional() });
export const zHasFab = z.object({ fabType: z.string().optional() });
export const zHasRangeSelector = z.object({ showRangeSelector: z.boolean().optional() });
export const zHasTitle = (title) => {
    return z.object({
        title: z.string().optional().default(title),
        hideTitle: z.boolean().optional(),
    });
};
export const zHasCountLimit = z.object({ showCountLimit: z.boolean().optional() });
export const zHasHideableCompanion = z.object({ hideCompanion: z.boolean().optional() });
export const zHasFlowStatus = z.object({
    showFlowStatus: z.boolean().optional().default(true),
});
export const zHasLookBackPeriod = z.object({
    showLookBackPeriod: z.boolean().optional().default(true),
});
export const zHasUndoButton = z.object({
    showUndoBtn: z.boolean().optional().default(false),
});
export const zHasSubheaderErrorText = z.object({ subheaderErrorText: z.string().optional() });
export const zHasConfigure = z.object({ showConfigure: z.boolean().optional() });
export const zHasFullHeight = z.object({ fullHeight: z.boolean().optional() });
export const zHasSummaries = z.object({ hideSummaries: z.boolean().optional() });
export const zHasWorkflow = z.object({
    /**
     * Optional workflow key that ties this view to `confdefn.workflows[workflow]`
     */
    workflow: z.string().optional(),
});
export const zIdentifierProps = z.object({
    keys: z.object({
        idProp: z.string(),
        descProp: z.string().optional(),
        styleId: z.string().optional(),
        leafIdProp: z.string().optional(),
    }),
});
export const zOptionalIdentifierProps = z.object({
    keys: z
        .object({
        idProp: z.string(),
        descProp: z.string().optional(),
        styleId: z.string().optional(),
        leafIdProp: z.string().optional(),
    })
        .optional(),
});
export const zGroupingInfo = z.object({
    groupingInfo: z.object({
        dataIndex: z.string(),
        staticColumns: z.array(z.string()),
    }),
});
export const zConfigApiParams = z.object({
    appName: z.union([z.literal(AppType.Assortment), z.literal(AppType.TDAnalysis)]),
    defnId: z.string().optional(),
    aggBy: z.string().optional(),
});
export const zConfigApi = z.object({
    url: z.string(),
    params: zConfigApiParams,
});
export const zConfigApiV2 = z.object({
    type: z.literal('viewdefnv2'),
    defnId: z.string(),
    allowCache: z.boolean().optional(),
});
export const zClientDataApi = z.object({
    url: z.string(),
    params: z.record(z.string(), z.string()).optional(),
    headers: z.record(z.string(), z.string()).optional(),
    isListData: z.literal(false).nullable().optional(),
    clientHandler: z.string().optional(),
    clientHandlerParams: z.string().optional(),
    email: z.string().optional(),
    method: z.enum(['post', 'get']).optional(),
});
export const zBaseListDataApiParams = z.object({
    /** If present, the default top level to potentially be prepended to existing aggBys from the view*/
    topAggBy: z.string().optional(),
    nestData: z.boolean().optional(),
    ignoreAncestors: z.boolean().optional(),
    includeLevelBy: z.boolean().optional(),
});
export const zHasSubheaderDownloadLink = z.object({
    subheader: z
        // this was causing excessive type length failures and I am doing this as I was unable to fix it
        // so I am obscuring the types for expediency
        .any()
        .optional(),
});
export const zListDataApi = z.object({
    defnId: z.string(),
    isListData: z.literal(true),
    params: z
        .object(Object.assign({ 
        /** The default bottom level to potentially be appended to existing aggBys from the view */
        aggBy: z.string() }, zBaseListDataApiParams.shape))
        // in this case, allow any extra params to pass through,
        // so that we support arbitrary params being passed down and through to the pivot
        .catchall(z.any()),
});
export const zRouteToLocationDataApi = z.object({
    url: z.string(),
    params: z.object({
        facet: z.string(),
        product: z.string(),
        location: z.string(),
        time: z.string(),
        prodlife: z.string(),
    }),
});
export const zDataApi = z.discriminatedUnion('isListData', [zClientDataApi, zListDataApi]);
export const isListDataApi = (dataApi) => {
    return (!_.isNil(dataApi) &&
        'isListData' in dataApi &&
        dataApi.isListData === true &&
        dataApi.defnId !== undefined);
};
// component property is merged in at the component level for a more narrow type (see confdefnComponents.ts)
export const zBaseSectionView = z.object({
    id: z.string(),
    name: z.string(),
    icon: z.string(),
    pathSlot: z.string(),
    inPerspectives: z.array(Perspective),
    disabled: z.boolean().optional(),
    hidden: z.boolean().optional(),
    overflow: z.string().optional(),
    tooltip: z.string().optional(),
    componentProps: z.object({}).default({}),
});
export var WorklistContextMenuAction;
(function (WorklistContextMenuAction) {
    WorklistContextMenuAction["add"] = "Add to Worklist";
    WorklistContextMenuAction["addSelected"] = "Add Selected to Worklist";
    WorklistContextMenuAction["snoozeDay"] = "Snooze until Next Day";
    WorklistContextMenuAction["snoozeDaySelected"] = "Snooze Selected until Next Day";
    WorklistContextMenuAction["snoozeWeek"] = "Snooze until Next Week";
    WorklistContextMenuAction["snoozeWeekSelected"] = "Snooze Selected until Next Week";
    WorklistContextMenuAction["markReviewed"] = "Mark as Reviewed";
    WorklistContextMenuAction["markReviewedSelected"] = "Mark Selected as Reviewed";
    WorklistContextMenuAction["remove"] = "Remove from Worklist";
    WorklistContextMenuAction["removeSelected"] = "Remove Selected from Worklist";
    WorklistContextMenuAction["reviewStyleDetails"] = "Review Style Details";
    WorklistContextMenuAction["editParams"] = "Edit Parameters";
    WorklistContextMenuAction["editPricingFlow"] = "Edit Pricing and Flow";
    WorklistContextMenuAction["poApproval"] = "PO Approval View";
})(WorklistContextMenuAction || (WorklistContextMenuAction = {}));
// Define the specific action types
const actionAddWorklist = z.object({
    type: z.literal('active'),
    icon: z.string().optional().default('fal fa-plus-circle'),
    text: z.string().optional().default(WorklistContextMenuAction.add),
    tooltip: z.string().optional().default('Add selected items to the worklist'),
});
const actionSnoozeDay = z.object({
    type: z.literal('snooze_1d'),
    icon: z.string().optional().default('fal fa-bed'),
    text: z.string().optional().default(WorklistContextMenuAction.snoozeDay),
    tooltip: z.string().optional().default('Snooze selected items until tomorrow'),
});
const actionSnoozeWeek = z.object({
    type: z.literal('snooze_1w'),
    icon: z.string().optional().default('fal fa-alarm-clock'),
    text: z.string().optional().default(WorklistContextMenuAction.snoozeWeek),
    tooltip: z.string().optional().default('Snooze selected items for seven days'),
});
const actionMarkReviewed = z.object({
    type: z.literal('reviewed'),
    icon: z.string().optional().default('fal fa-clipboard-check'),
    text: z.string().optional().default(WorklistContextMenuAction.markReviewed),
    tooltip: z.string().optional().default('Mark selected items as reviewed'),
});
const actionRemove = z.object({
    type: z.literal('dropped'),
    icon: z.string().optional().default('fal fa-minus-circle'),
    text: z.string().optional().default(WorklistContextMenuAction.remove),
    tooltip: z.string().optional().default('Remove selected items from the worklist'),
});
const actionReviewStyleDetails = z.object({
    type: z.literal('reviewStyleDetails'),
    icon: z.string().optional().default('fal fa-binoculars'),
    text: z.string().optional().default(WorklistContextMenuAction.reviewStyleDetails),
    tooltip: z.string().optional().default('Review style details for selected items'),
});
const actionFailed = z.object({
    type: z.literal('failed'),
    error: z.any(), // zod4 can't convert this type based on instance, needs to be something else
});
const separator = z.object({
    type: z.literal('separator'),
});
// Combine all actions using discriminated union
export const HasActionButtonsMenu = z.discriminatedUnion('type', [
    actionAddWorklist,
    actionSnoozeDay,
    actionSnoozeWeek,
    actionMarkReviewed,
    actionRemove,
    separator,
    actionReviewStyleDetails,
    actionFailed,
]);
export const defaultWorklistButtons = [
    actionAddWorklist.parse({ type: 'active' }),
    actionSnoozeDay.parse({ type: 'snooze_1d' }),
    actionSnoozeWeek.parse({ type: 'snooze_1w' }),
    actionMarkReviewed.parse({ type: 'reviewed' }),
    actionRemove.parse({ type: 'dropped' }),
];
const zWorklistConfigButtons = z.array(HasActionButtonsMenu);
